/* global mixpanel */
import React, { Component } from "react"
import { Switch, Route } from "react-router-dom"
import { connect } from "react-redux"
import { parse } from "query-string"
import * as actionCreators from "./redux/action_creators"
import * as jwtUtils from "./util/jwt_utils"
import { getItemsArray } from "./util/prop_utils"
import * as appMap from "./app_map"
import { isToday } from "./util/date_utils"
import { isSafari } from "./util/random_utils"
import { confirmHighDuration } from "./util/time_utils"
import NotFound from "./pages/not_found"
import TopNav from "./components/layout/top_nav"
import TimeEntryModal from "./components/modals/time_entry_modal"
import GA from "react-ga"

import store from "./redux/store"
import { sitka } from "./redux/store"
import * as client from "./api/client"
import * as config from './config'
import { createOAuthPopupWindow } from "./util/oauth_utils"
import { shouldRestorePersistedNewEntryFields } from "./util/time_entry_draft_utils"

// setup analytics
const googleAnalyticsKey = config.googleAnalyticsKey
GA.initialize(googleAnalyticsKey)

const trackPage = (page) => {
    GA.pageview(page)
}

client.setUnauthHandler(() => {
    store.dispatch(actionCreators.signOut())
    return
})

require("../node_modules/@blueprintjs/core/dist/blueprint.css")

class App extends Component {
    constructor(props) {
        super(props)
        this.newTimeEntry = this.newTimeEntry.bind(this)
        this.startTimer = this.startTimer.bind(this)
        this.editTimeEntry = this.editTimeEntry.bind(this)
        this.stopTimeEntry = this.stopTimeEntry.bind(this)
        this.closeTimeEntry = this.closeTimeEntry.bind(this)
        this.editTeam = this.editTeam.bind(this)
        this.signOut = this.signOut.bind(this)
        this.selectTeam = this.selectTeam.bind(this)
        this.closeTeamModal = this.closeTeamModal.bind(this)
        this.isPersonalTeam = this.isPersonalTeam.bind(this)
        this.updateMobileVisibility = this.updateMobileVisibility.bind(this)
        this.listenForPauseAndResume = this.listenForPauseAndResume.bind(this)
        this.handleOauth = this.handleOauth.bind(this)
        this.persistNewEntryDraft = this.persistNewEntryDraft.bind(this)
        this.clearPersistedNewEntryDraft = this.clearPersistedNewEntryDraft.bind(
            this,
        )

        this.state = {
            timeEntryModal: false,
            teamModalVisible: false,
            isStartTimer: false,
            editTeamId: null,
            selectedEntryId: null,
            defaultTags: null,
            defaultStartAt: null,
            showTimeFields: false,
            teams: null,
            isMobile: this.getIsMobile(),
            /** Tags + description for unsaved "new entry" modal between opens */
            newEntryDraft: null,
        }
    }

    persistNewEntryDraft(draft) {
        this.setState({ newEntryDraft: draft })
    }

    clearPersistedNewEntryDraft() {
        this.setState({ newEntryDraft: null })
    }

    componentWillMount() {
        const {
            bootstrapped,
            setPaymentModalOpen,
            setUiNotificationPreferences,
            setSelectedSettingsTab,
        } = this.props
        const expired = jwtUtils.isExpired()
        // get email from query param
        const query = parse(this.props.location.search)
        const { t, action } = query
        const { subject: emailFromQuery } = jwtUtils.getPayload(t)

        if (action === "upgrade") {
            // open payment menu on component mount (or after login)
            setPaymentModalOpen(true)
        }

        if (action === "notificationPreferences") {
            // set notificationPreferences
            setUiNotificationPreferences(true)
        }

        if (action === "revokeAuthorization") {
            setSelectedSettingsTab("connected apps")
        }

        // Check by the email is a different user than currently logged in
        const currentUserEmail = jwtUtils.getEmail("jwt-token")
        const isChangingUser = emailFromQuery
            ? currentUserEmail !== emailFromQuery
            : false

        if (!bootstrapped && !expired && !isChangingUser) {
            this.props.startBootstrap()
        }

        if (isChangingUser) {
            this.props.signOut()
        }
    }

    componentWillReceiveProps(nextProps) {
        const {
            bootstrapped,
            ui,
            setUiNotificationPreferences,
            history,
        } = nextProps
        const currentUser = jwtUtils.getEmail("jwt-token")
        const nextUser = nextProps.profile.email.address

        if (currentUser !== nextUser && !!nextUser && !bootstrapped) {
            this.props.startBootstrap()
        }

        // redirect to settings if notificationPreferences is true
        if (
            bootstrapped &&
            !this.props.bootstrapped &&
            ui.notificationPreferences
        ) {
            if (this.props.location.pathname === "/settings") {
                // if on settings page, clear notification preferences
                setUiNotificationPreferences(false)
            } else {
                history.push("/settings")
            }
        }
    }

    componentDidMount() {
        const { location, history } = this.props
        // GA Page Tracking
        const page = location.pathname + location.search
        trackPage(page)
        
        const { pathname } = history.location
        this.updateMobileVisibility()
        this.listenForPauseAndResume()
        window.addEventListener("resize", this.updateMobileVisibility)
        
        // do not show Zendesk support widget if page is show body only
        const showZendeskWidget = !appMap.isPathShowBodyOnlyPage(pathname)
        if (showZendeskWidget) {
            const script = document.createElement("script")
            script.src = "https://static.zdassets.com/ekr/snippet.js?key=fce5d685-8416-4254-8457-d080e951fe87"
            script.async = true
            script.id = "ze-snippet"
            document.body.appendChild(script)
        }
    }

    componentDidUpdate(prevProps) {
        const { pathname, search } = prevProps.location
        const { location } = this.props
        // GA Page Tracking
        const currentPage = pathname + search
        const nextPage = location.pathname + location.search

        if (currentPage !== nextPage) {
            trackPage(nextPage)
        }
    }

    getIsMobile() {
        return window.innerWidth <= 800
    }

    updateMobileVisibility() {
        this.setState({ isMobile: this.getIsMobile() })
    }

    listenForPauseAndResume() {
        const getTime = () => {
            return new Date().getTime()
        }

        let lastInterval = getTime()
        let lastOnlineStatus = window.navigator.onLine

        const intervalHeartbeat = () => {
            // compare time difference
            const now = getTime()
            const diff = now - lastInterval
            const offBy = diff - 1000 // 1000 = the 1 second delay I was expecting
            lastInterval = now

            // compare network status difference
            const currentOnlineStatus = window.navigator.onLine
            const backOnline =
                lastOnlineStatus === false && currentOnlineStatus === true
            lastOnlineStatus = currentOnlineStatus

            if (offBy > 2000 || backOnline) {
                // don't trigger on small stutters less than 2000ms (2 seconds)
                if (offBy > 2000) {
                    console.log("Interval heartbeat - off by " + offBy + "ms") // eslint-disable-line no-console
                }
                if (backOnline) {
                    console.log("Internet is back online") // eslint-disable-line no-console
                }
                const currentUser = jwtUtils.getEmail("jwt-token")
                if (currentUser) {
                    this.props.startBootstrap()
                    this.props.fetchCurrentHorizon()
                }
            }
        }

        setInterval(intervalHeartbeat, 1000)
    }

    newTimeEntry(defaultTags) {
        // default time record start time to beginning of the day
        // if the currently selected day isn't today and we're at a day interval
        const {
            viewport: {
                range: { interval, start: startTs },
            },
        } = this.props
        const defaultStartAt =
            interval === "Day" && !isToday(startTs) ? startTs : Date.now()

        const hasExplicitDefaultTags =
            defaultTags && defaultTags.length > 0

        this.setState(prev => ({
            timeEntryModal: true,
            showTimeFields: true,
            isStartTimer: false,
            defaultStartAt,
            defaultTags: hasExplicitDefaultTags ? defaultTags : null,
            ...(hasExplicitDefaultTags ? { newEntryDraft: null } : {}),
        }))

        mixpanel.track("Open new time entry")
        GA.event({category: "User", action: "Open new time entry"})
    }

    startTimer(defaultTags) {
        this.setState({
            timeEntryModal: true,
            isStartTimer: true,
            defaultStartAt: new Date(),
            defaultTags: defaultTags || null,
            showTimeFields: false,
        })
        mixpanel.track("Open new timer")
        GA.event({category: "User", action: "Open new timer"})
    }

    openTimeEntryModal = runningEntry => {
        this.setState({
            timeEntryModal: true,
            isStartTimer: false,
            defaultStartAt: runningEntry.startAt,
            defaultTags: runningEntry.tags,
            showTimeFields: true,
            selectedEntryId: runningEntry.id,
        })
    }

    stopTimeEntry(runningEntry) {
        if (!confirmHighDuration(runningEntry)) {
            this.openTimeEntryModal(runningEntry)
            return
        }

        this.props.stopTimeEntry(runningEntry)
        // TODO: Multiteam
        const { userId: creatorId, teamId, tags = [] } = runningEntry
        mixpanel.track("Stop timer - top nav", {
            teamId,
            creatorId,
            tagCount: tags.length,
        })
        GA.event({category: "User", action: "Stop timer"})
    }

    editTimeEntry(id) {
        this.setState({
            timeEntryModal: true,
            isStartTimer: false,
            defaultTags: null,
            showTimeFields: true,
            selectedEntryId: id,
        })
    }

    closeTimeEntry(callback) {
        this.setState(
            {
                timeEntryModal: false,
                isStartTimer: false,
                selectedEntryId: null,
            },
            callback,
        )
    }

    editTeam(id) {
        this.setState({
            teamModalVisible: true,
            editTeamId: id,
        })
    }

    closeTeamModal() {
        this.setState({
            teamModalVisible: false,
        })
    }

    signOut() {
        this.props.signOut()
    }

    isPersonalTeam(teamId) {
        return this.props.profile.personalTeamId === teamId
    }

    selectTeam(teamId) {
        const personalTeam = this.isPersonalTeam(teamId)
        const goTo = personalTeam ? "/dashboard" : "/teams/" + teamId

        // TODO: check current route and only navigate if not already there
        this.props.history.push(goTo)
    }

    navigateToSettingsPage = history => {
        this.props.handleNavigateAndFetchAccountCustomer(history)
    }

    viewerIsTeamAdmin = () => {
        const { profile } = this.props
        const { teamId } = this.props.viewport
        const viewerTeamMembership = profile && profile.memberships[teamId]
        return (
            !!viewerTeamMembership &&
            viewerTeamMembership.accessLevel === "Admin"
        )
    }

    handleOauth(service) {
        const childWindow = createOAuthPopupWindow()
        const { oauth } = sitka.getModules()
        oauth.handleStartOAuth(childWindow, service)
    }

    pageBlankWrapper = child => {
        const isLoggedIn = jwtUtils.getEmail("jwt-token") ? "logged-in " : ""

        return (
            <div
                id="app-container-v2"
                className={`light-bg ${isLoggedIn} ${isSafari() &&
                    "app-safari"}`}
            >
                {child}
            </div>
        )
    }

    pageWrapper = () => {
        const { pathname } = this.props.history.location
        return appMap.isPathShowBodyOnlyPage(pathname) 
            ? this.pageBlankWrapper 
            : this.defaultPageWrapper
    }

    OauthDevTest = () => {
        return(
            <div>
                OAUTH:
                <span onClick={() => this.handleOauth("harvest")}> Harvest </span>
                <span onClick={() => this.handleOauth("jira")}> | JIRA </span>
                <span onClick={() => this.handleOauth("slack")}> | Slack </span>
                <span onClick={() => this.handleOauth("quickbooks")}> | QuickBooks </span>
                <span onClick={() => this.handleOauth("google")}> | Google </span>
                <span onClick={() => this.handleOauth("zoom")}> | Zoom </span>
                <span onClick={() => this.handleOauth("github")}> | Github </span>
                <span onClick={() => this.handleOauth("shoutbase")}> | Shoutbase </span>
            </div>
        ) 
    }

    defaultPageWrapper = child => {
        const {
            createTimeEntry,
            updateTimeEntry,
            deleteTimeEntry,
            teams,
            profile,
            viewport,
            changePassword,
            ui,
            setUiPwModalOpen,
            customer,
            setPaymentModalOpen,
            isLoading,
            handleChangePaymentMethod,
            handleStripeToken,
            accounts,
            sendBulkInvites,
            customer: { paymentStatus, trialEnd },
        } = this.props
        const {
            timeEntryModal,
            isMobile,
            selectedEntryId,
            isStartTimer,
            showTimeFields,
            defaultTags,
            newEntryDraft,
        } = this.state
        const restorePersistedNewEntryFields = shouldRestorePersistedNewEntryFields(
            {
                selectedEntryId,
                isStartTimer,
                showTimeFields,
                defaultTags,
            },
        )
        const teamsMap = teams.items
        const teamsList = getItemsArray(teams)
        const selectedTeamId = viewport.teamId
        const viewerIsTeamAdmin = this.viewerIsTeamAdmin()
        const account = accounts.items[profile.accountId]
        const isLoggedIn = jwtUtils.getEmail("jwt-token") ? "logged-in " : ""
        
        return (
            <div
                id="app-container-v2"
                className={`light-bg ${isLoggedIn} ${isSafari() &&
                    "app-safari"}`}
            >
                <TopNav
                    teams={teamsList}
                    onNewTimeEntry={this.newTimeEntry}
                    onStartTimer={this.startTimer}
                    setForceV1={this.props.setForceV1}
                    history={this.props.history}
                    profile={profile}
                    signOut={this.signOut}
                    changePassword={changePassword}
                    tags={this.props.tags}
                    timeEntries={this.props.timeEntries}
                    runningTimeEntries={this.props.runningTimeEntries}
                    editTimeEntry={this.editTimeEntry}
                    stopTimeEntry={this.stopTimeEntry}
                    selectTeam={this.selectTeam}
                    selectedTeamId={selectedTeamId}
                    createTeam={this.props.createTeam}
                    editTeam={this.editTeam}
                    editTeamId={this.state.editTeamId}
                    teamModalVisible={this.state.teamModalVisible}
                    closeTeamModal={this.closeTeamModal}
                    putTeam={this.props.updateTeam}
                    ui={ui}
                    setUiPwModalOpen={setUiPwModalOpen}
                    isMobile={isMobile}
                    paymentStatus={paymentStatus}
                    navigateToSettingsPage={this.navigateToSettingsPage}
                    trialEnd={trialEnd}
                    account={account}
                    sendBulkInvites={sendBulkInvites}
                    setPaymentModalOpen={setPaymentModalOpen}
                    customer={customer}
                    isLoading={isLoading}
                    handleChangePaymentMethod={handleChangePaymentMethod}
                    handleStripeToken={handleStripeToken}
                />
                {/* {this.OauthDevTest()} */}
                {child}
                {timeEntryModal && (
                    <TimeEntryModal
                        {...this.props}
                        teams={teamsMap}
                        setPaymentModalOpen={setPaymentModalOpen}
                        onNewTimeEntry={this.newTimeEntry}
                        onStartTimer={this.startTimer}
                        isStartTimer={isStartTimer}
                        createTimeEntry={createTimeEntry}
                        updateTimeEntry={updateTimeEntry}
                        deleteTimeEntry={deleteTimeEntry}
                        onClose={this.closeTimeEntry}
                        selectedEntryId={selectedEntryId}
                        defaultStartAt={this.state.defaultStartAt}
                        defaultTags={defaultTags}
                        showTimeFields={showTimeFields}
                        newEntryDraft={newEntryDraft}
                        restorePersistedNewEntryFields={
                            restorePersistedNewEntryFields
                        }
                        onPersistNewEntryDraft={this.persistNewEntryDraft}
                        onClearPersistedNewEntryDraft={
                            this.clearPersistedNewEntryDraft
                        }
                        selectedTeamId={
                            selectedTeamId === profile.personalTeamId
                                ? ""
                                : selectedTeamId
                        }
                        viewerIsTeamAdmin={viewerIsTeamAdmin}
                        isMobile={isMobile}
                    />
                )}
            </div>
        )
    }

    render() {
        const {
            createTag,
            updateTag,
            deleteTag,
            clearTagReports,
            queryTagReports,
            profile,
        } = this.props
        const { isMobile } = this.state
        const { onboardingStep } = profile

        const { navigation } = sitka.getModules()
        const { handleSetRedirect, handleClearRedirect } = navigation

        const modalActions = {
            newTimeEntry: this.newTimeEntry,
            newTimer: this.startTimer,
            editTimeEntry: this.editTimeEntry,
            createTag: createTag,
            updateTag: updateTag,
            deleteTag: deleteTag,
            clearTagReports: clearTagReports,
            queryTagReports: queryTagReports,
            openTimeEntryModal: this.openTimeEntryModal,
            isMobile: isMobile,
            handleSetRedirect,
            handleClearRedirect,
            navigationState: this.props.navigation,
        }

        return this.pageWrapper()(
            <Switch>
                {appMap.routes("", modalActions, onboardingStep)}
                <Route path="*" component={NotFound} />
            </Switch>,
        )
    }
}

function mapStateToProps(state) {
    return state
}

export default connect(
    mapStateToProps,
    actionCreators,
)(App)



// WEBPACK FOOTER //
// ./src/app.jsx